import React from "react";
import { Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { Table, TD, TH, TR } from "@ag-media/react-pdf-table";
import _ from "lodash";
import {
    capitalExpenditureConfig,
    capitalizationRateConfig,
    fixedExpenseConfig,
    getFooter,
    operatingIncomeConfig,
    otherIncomeConfig,
    SpaceData,
    variableExpenseConfig,
} from "../../../utils/helperUtils";
import { formatCurrencyBlankUSD, formatPercentage } from "../../../utils/formattingUtils";

const styles = StyleSheet.create({
    page: {
        padding: "40px 50px",
        fontSize: 10,
        backgroundColor: "#ffffff",
    },
    headerSection: {
        marginBottom: 10,
        paddingBottom: 6,
    },
    heading: {
        fontSize: 16,
        fontWeight: "bold",
        marginBottom: 6,
        textAlign: "center",
        color: "#2c3e50",
    },
    subHeading: {
        fontSize: 12,
        color: "#34495e",
        textAlign: "left",
        marginBottom: 4,
        marginTop: 6,
    },
    subHeadingSpaceType: {
        fontSize: 14,
        color: "#34495e",
        textAlign: "center",
        marginBottom: 2,
        marginTop: 4,
    },
    section: {
        marginBottom: 9,
        borderRadius: 2,
        padding: 6,
    },
    tableContainer: {
        marginTop: 4,
        marginBottom: 4,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    tableHeader: {
        fontSize: 14,
        backgroundColor: "#f7f6f8",
    },
    tableCell: {
        textAlign: "center",
        fontSize: 10,
        padding: 5,
        borderBottom: "1px solid #e0e0e0",
    },
    totalRow: {
        backgroundColor: "#e9ecef",
        fontWeight: "bold",
    },
    address: {
        fontSize: 10,
        textAlign: "center",
        color: "#555555",
        marginBottom: 3,
    },
});

interface AppealData {
    appealApproachCalculationData: {
        income: {
            spaces: {
                [key: string]: SpaceData;
            };
        };
        exportAppealsPackage: {
            coverPage: {
                subjectPropertyNumber: string;
                subjectPropertyAddress: {
                    streetaddress: string;
                    city: string;
                    state: string;
                    zipCode: string;
                };
            };
        };
    };
}

interface IncomeAndExpenseProFormaPageProps {
    data: AppealData;
    confidentialInfo: string;
}

interface TableHeaderProps {
    titles: string[];
}

interface TableRowsProps {
    rows: string[][];
}

interface SectionTableProps {
    title: string;
    headers: string[];
    rows: string[][];
}

interface TableSectionProps extends SectionTableProps {
    estimatedHeight: number;
}

interface SummaryRowProps {
    label: string;
    percentage?: number;
    amount: number;
}

interface PropertyHeaderProps {
    propertyNumber: string;
    address: {
        streetaddress: string;
        city: string;
        state: string;
        zipCode: string;
    };
}

interface PageBreakAwareSectionProps {
    children: React.ReactNode;
    minHeight: number;
}

const MAX_PAGE_HEIGHT = 700;

const SECTION_HEIGHTS = {
    header: 100,
    operatingIncome: 150,
    otherIncome: 120,
    effectiveGrossIncome: 30,
    fixedExpenses: 180,
    variableExpenses: 200,
    capitalExpenditure: 100,
    capitalizationRate: 80,
    summaryRows: 40,
};

const PageBreakAwareSection: React.FC<PageBreakAwareSectionProps> = ({ children, minHeight }) => (
    <View style={{ ...styles.tableContainer, minHeight }} break>
        {children}
    </View>
);

const TableHeaderRow: React.FC<TableHeaderProps> = ({ titles }) => (
    <TR>
        {titles.map((title, index) => (
            <TD key={index} style={{ ...styles.tableCell, minWidth: index === 0 ? "35%" : "auto" }}>
                {title}
            </TD>
        ))}
    </TR>
);

const TableRows: React.FC<TableRowsProps> = ({ rows }) => (
    <>
        {rows.map((row, rowIndex) => (
            <TR key={rowIndex}>
                {row.map((cell, index) => (
                    <TD
                        key={index}
                        style={{ ...styles.tableCell, minWidth: index === 0 ? "35%" : "auto" }}
                    >
                        {cell}
                    </TD>
                ))}
            </TR>
        ))}
    </>
);

const SectionTable: React.FC<SectionTableProps> = ({ title, headers, rows }) => (
    <View style={styles.tableContainer}>
        <Table style={{ border: "none" }}>
            <TH>
                <TD
                    style={{
                        ...styles.tableCell,
                        ...styles.tableHeader,
                        width: "100%",
                        textAlign: "left",
                        borderRight: "none", // Remove right border
                    }}
                >
                    {title}
                </TD>
            </TH>
            <TableHeaderRow titles={headers} />
            <TableRows rows={rows} />
        </Table>
    </View>
);

const TableSection: React.FC<TableSectionProps> = ({ title, headers, rows, estimatedHeight }) => (
    <PageBreakAwareSection minHeight={estimatedHeight}>
        <SectionTable title={title} headers={headers} rows={rows} />
    </PageBreakAwareSection>
);

const SummaryRow: React.FC<SummaryRowProps> = ({ label, percentage, amount }) => (
    <Table style={{ border: "none" }}>
        <TH style={styles.tableHeader}>
            <TD style={{ ...styles.tableCell, minWidth: "35%", borderRight: "none" }}>{label}</TD>
            <TD style={{ ...styles.tableCell, borderRight: "none" }}></TD>
            <TD style={{ ...styles.tableCell, borderRight: "none" }}>
                {percentage ? formatPercentage(percentage) : ""}
            </TD>
            <TD style={{ ...styles.tableCell, borderRight: "none" }}>
                {formatCurrencyBlankUSD(amount)}
            </TD>
        </TH>
    </Table>
);

const PropertyHeader: React.FC<PropertyHeaderProps> = ({ propertyNumber, address }) => (
    <View style={styles.headerSection}>
        <Text style={styles.heading}>{propertyNumber}</Text>
        <Text style={styles.address}>{address.streetaddress}</Text>
        <Text style={styles.address}>
            {address.city}, {address.state} {address.zipCode}
        </Text>
    </View>
);

export const IncomeAndExpenseProFormaPage: React.FC<IncomeAndExpenseProFormaPageProps> = ({
    data,
    confidentialInfo,
}) => {
    const spaces = _.get(data, "appealApproachCalculationData.income.spaces", {});
    const propertyData = _.get(
        data,
        "appealApproachCalculationData.exportAppealsPackage.coverPage",
        {}
    );

    const createPage = (
        spaceKey: string,
        pageIndex: number,
        content: React.ReactNode,
        isFirstPage: boolean
    ) => (
        <Page key={`${spaceKey}-page-${pageIndex}`} size="A4" style={styles.page}>
            {isFirstPage && (
                <PropertyHeader
                    propertyNumber={propertyData.subjectPropertyNumber}
                    address={propertyData.subjectPropertyAddress}
                />
            )}
            {content}
            {getFooter(confidentialInfo)}
        </Page>
    );

    return (
        <>
            {Object.entries(spaces).map(([spaceKey, space]: [string, SpaceData]) => {
                if (!space.enable) return null;

                const { incomeExpenseProForma: iep } = space;
                let currentPageSections: React.ReactNode[] = [];
                const pages: React.ReactNode[] = [];
                let currentPageHeight = 0;

                const addSection = (section: React.ReactNode, height: number) => {
                    if (currentPageHeight + height > MAX_PAGE_HEIGHT) {
                        // Create new page with accumulated sections
                        pages.push(
                            createPage(
                                spaceKey,
                                pages.length,
                                <View style={styles.section}>{currentPageSections}</View>,
                                pages.length === 0
                            )
                        );
                        currentPageSections = [];
                        currentPageHeight = 0;
                    }

                    currentPageSections.push(section);
                    currentPageHeight += height;
                };

                if (pages.length === 0) {
                    addSection(
                        <View style={styles.section}>
                            <Text style={styles.heading}>Income & Expense Pro Forma</Text>
                            <Text style={styles.subHeadingSpaceType}>
                                {space.spaceDetails.spaceType}
                            </Text>
                        </View>,
                        SECTION_HEIGHTS.header
                    );
                }

                addSection(
                    <TableSection
                        title="Operating Income"
                        headers={["Operating Income", "$/SF", "SF", "$"]}
                        rows={operatingIncomeConfig.getRows(space)}
                        estimatedHeight={SECTION_HEIGHTS.operatingIncome}
                    />,
                    SECTION_HEIGHTS.operatingIncome
                );

                addSection(
                    <TableSection
                        title="Other Income"
                        headers={["Other Income", "", "", "$"]}
                        rows={otherIncomeConfig.getRows(space)}
                        estimatedHeight={SECTION_HEIGHTS.otherIncome}
                    />,
                    SECTION_HEIGHTS.otherIncome
                );

                addSection(
                    <PageBreakAwareSection minHeight={SECTION_HEIGHTS.effectiveGrossIncome}>
                        <SummaryRow
                            label="Effective Gross Income"
                            amount={iep.operatingIncome.effectiveGrossIncome}
                        />
                    </PageBreakAwareSection>,
                    SECTION_HEIGHTS.effectiveGrossIncome
                );

                addSection(
                    <TableSection
                        title="Fixed Expenses"
                        headers={["Fixed Expense", "", "% of EGI", "$"]}
                        rows={fixedExpenseConfig.getRows(space)}
                        estimatedHeight={SECTION_HEIGHTS.fixedExpenses}
                    />,
                    SECTION_HEIGHTS.fixedExpenses
                );

                addSection(
                    <TableSection
                        title="Variable Expenses"
                        headers={["Variable Expense", "", "% of EGI", "$"]}
                        rows={variableExpenseConfig.getRows(space)}
                        estimatedHeight={SECTION_HEIGHTS.variableExpenses}
                    />,
                    SECTION_HEIGHTS.variableExpenses
                );

                addSection(
                    <PageBreakAwareSection minHeight={SECTION_HEIGHTS.summaryRows}>
                        <SummaryRow
                            label="Total Operating Expenses"
                            percentage={iep.fixedExpenses.subtotal + iep.variableExpenses.subtotal}
                            amount={iep.totalOperatingExpenses}
                        />
                    </PageBreakAwareSection>,
                    SECTION_HEIGHTS.summaryRows
                );

                addSection(
                    <TableSection
                        title="Capital Expenditure"
                        headers={["Capital Expenditure", "", "% of EGI", "$"]}
                        rows={capitalExpenditureConfig.getRows(space)}
                        estimatedHeight={SECTION_HEIGHTS.capitalExpenditure}
                    />,
                    SECTION_HEIGHTS.capitalExpenditure
                );

                const remainingSummaries = [
                    <SummaryRow
                        label="Total Expenses"
                        percentage={
                            iep.fixedExpenses.subtotal +
                            iep.variableExpenses.subtotal +
                            iep.capitalExpenditure.subtotal
                        }
                        amount={iep.totalExpenseLoad}
                    />,
                    <SummaryRow
                        label="Net Operating Income"
                        percentage={
                            100 -
                            (iep.fixedExpenses.subtotal +
                                iep.variableExpenses.subtotal +
                                iep.capitalExpenditure.subtotal)
                        }
                        amount={iep.netOperatingIncome}
                    />,
                    <TableSection
                        title="Capitalization Rate"
                        headers={["Capitalization Rate", "", "%", ""]}
                        rows={capitalizationRateConfig.getRows(space)}
                        estimatedHeight={SECTION_HEIGHTS.capitalizationRate}
                    />,
                    <SummaryRow label="OAR" percentage={iep.oar} amount={0} />,
                    <SummaryRow label="Indicated Value" amount={iep.indicatedValue} />,
                    <SummaryRow label="Rent Loss Concession" amount={iep.rentLossConcession} />,
                    <SummaryRow label="Adjusted Value" amount={iep.adjustedValue} />,
                ];

                remainingSummaries.forEach(summary => {
                    addSection(summary, SECTION_HEIGHTS.summaryRows);
                });

                if (currentPageSections.length > 0) {
                    pages.push(
                        createPage(
                            spaceKey,
                            pages.length,
                            <View style={styles.section}>{currentPageSections}</View>,
                            pages.length === 0
                        )
                    );
                }

                return pages;
            })}
        </>
    );
};
